import Papa from 'papaparse';
import { Directionality } from 'BMapsSrc/integrations/properties_common';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

// Allowed bracketed tokens for REINVENT's mol2mol_scaffold_generic model.
const ALLOWED_BRACKET_TOKENS = new Set([
    '[11CH3]', '[11C]', '[11c]', '[123I]', '[18F]', '[19F]', '[76Br]',
    '[C@@H]', '[C@@]', '[C@H]', '[C@]',
    '[N+]', '[N@+]', '[N@@+]', '[O-]', '[O]',
    '[S@@]', '[S@]', '[n+]', '[n-]', '[nH]',
]);

const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            reinvent: {
                metadata: {
                    label: 'Reinvent properties',
                    tooltipHeading: 'Reinvent properties',
                },
                properties: {
                    SMILES: {
                        metadata: {
                            label: 'Compound Structure',
                            description: 'The generated molecule. Hover over it to enlarge it.',
                            structureValue: true,
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    smilesText: {
                        metadata: {
                            label: 'SMILES String',
                            description: 'The generated molecule, as plain-text SMILES.',
                            type: 'string',
                            defaultVisible: false,
                            excludeFromImport: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    Score: {
                        metadata: {
                            label: 'Score',
                            description: 'Composite multi-parameter optimization score after combining all objectives and transforms.',
                            unit: 'Score (0-1)',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.7',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    Agent: {
                        metadata: {
                            label: 'Agent',
                            description: 'Model (policy) log-likelihood of generating the SMILES under the current agent (higher/less negative is better).',
                            unit: 'log-likelihood (nats)',
                            directionality: Directionality.MAXIMIZE,
                            range: 'Unbounded (typically -50 to 0)',
                            goodRange: '>-10',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    Prior: {
                        metadata: {
                            label: 'Prior',
                            description: 'Reference model (prior) log-likelihood of the SMILES; used to regularize against distributional drift.',
                            unit: 'log-likelihood (nats)',
                            directionality: Directionality.MAXIMIZE,
                            range: 'Unbounded (typically -50 to 0)',
                            goodRange: '>-10',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    Target: {
                        metadata: {
                            label: 'Target',
                            description: 'Combined objective score before final scaling (depends on your scoring function setup).',
                            unit: 'Score (0-1)',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.7',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },

                    'Molecular weight': {
                        metadata: {
                            label: 'Weighted Molecular Weight',
                            description: 'Sum of atomic masses for the molecule.',
                            unit: 'Da',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '50 - 1000',
                            goodRange: '200 - 500',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'Number of HB acceptors (Lipinski)': {
                        metadata: {
                            label: 'Weighted H-Bond Acceptors (Lipinski)',
                            description: 'Count of hydrogen bond acceptors as defined by Lipinski.',
                            unit: 'count',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 20',
                            goodRange: '≤10',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'Number of HB donors (Lipinski)': {
                        metadata: {
                            label: 'Weighted H-Bond Donors (Lipinski)',
                            description: 'Count of hydrogen bond donors as defined by Lipinski.',
                            unit: 'count',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 10',
                            goodRange: '≤5',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'QED Score': {
                        metadata: {
                            label: 'Weighted QED Score',
                            description: 'Quantitative estimate of drug-likeness combining multiple physicochemical properties.',
                            unit: 'Score (0-1)',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.6',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'SA score': {
                        metadata: {
                            label: 'Weighted Synthetic Accessibility',
                            description: 'Heuristic estimate of ease of synthesis; lower values indicate easier synthesis.',
                            unit: 'SA (1-10)',
                            directionality: Directionality.MINIMIZE,
                            range: '1 - 10',
                            goodRange: '<6',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    TPSA: {
                        metadata: {
                            label: 'Weighted TPSA',
                            description: 'Sum of surfaces of polar atoms; correlates with absorption, BBB penetration, and permeability.',
                            unit: 'Å²',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 250',
                            goodRange: '20 - 130',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'SlogP (RDKit)': {
                        metadata: {
                            label: 'Weighted LogP (RDKit)',
                            description: 'Octanol/water partition coefficient estimate (lipophilicity).',
                            unit: 'logP',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '-2 - 8',
                            goodRange: '1 - 3',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },

                    'Molecular weight (raw)': {
                        metadata: {
                            label: 'Molecular Weight',
                            description: 'Unnormalized molecular weight prior to any scaling or transforms.',
                            unit: 'Da',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '50 - 1000',
                            goodRange: '200 - 500',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'Number of HB acceptors (Lipinski) (raw)': {
                        metadata: {
                            label: 'H-Bond Acceptors (Lipinski)',
                            description: 'Unnormalized acceptor count.',
                            unit: 'count',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 20',
                            goodRange: '≤10',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'Number of HB donors (Lipinski) (raw)': {
                        metadata: {
                            label: 'H-Bond Donors (Lipinski)',
                            description: 'Unnormalized donor count.',
                            unit: 'count',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 10',
                            goodRange: '≤5',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'SA score (raw)': {
                        metadata: {
                            label: 'Synthetic Accessibility',
                            description: 'Unnormalized Ertl SA score; lower is easier to synthesize.',
                            unit: 'SA (1-10)',
                            directionality: Directionality.MINIMIZE,
                            range: '1 - 10',
                            goodRange: '<6',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'QED Score (raw)': {
                        metadata: {
                            label: 'QED Score',
                            description: 'Unnormalized QED value prior to final scaling.',
                            unit: 'Score (0-1)',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.6',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'SlogP (RDKit) (raw)': {
                        metadata: {
                            label: 'LogP (RDKit)',
                            description: 'Unnormalized logP value prior to transforms.',
                            unit: 'logP',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '-2 - 8',
                            goodRange: '1 - 3',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    'TPSA (raw)': {
                        metadata: {
                            label: 'TPSA',
                            description: 'Unnormalized TPSA prior to transforms.',
                            unit: 'Å²',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0 - 250',
                            goodRange: '20 - 130',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },

                    ...[
                        'GraphLength',
                        'NumAtomStereoCenters',
                        'NumRotBond',
                        'Csp3',
                        'Numsp',
                        'Numsp2',
                        'Numsp3',
                        'NumHeavyAtoms',
                        'NumHeteroAtoms',
                        'NumRings',
                        'NumAromaticRings',
                        'NumAliphaticRings',
                    ].reduce((acc, propName) => {
                        const regularTag = `${propName} (RDKit)`;
                        const rawTag = `${regularTag} (raw)`;

                        acc[regularTag] = {
                            metadata: {
                                label: regularTag,
                                excludeFromTable: true,
                                propApplicability: PropApplicability.PERCOMPOUND,
                            },
                        };

                        acc[rawTag] = {
                            metadata: {
                                label: regularTag,
                                propApplicability: PropApplicability.PERCOMPOUND,
                            },
                        };

                        return acc;
                    }, {}),
                },
            },
        },
    },
});

/**
 * REINVENT 4 generative job: parses generated/scored compounds from the
 * results CSV and stores them on local job state for the results table.
 *
 * Owns `jobForm` (the field schema consumed by `JobForm`/`FieldList`) and
 * the submission methods below alongside its results/dispatch logic — data
 * and plain logic only, no rendering; `JobForm`/`FieldList` own all JSX.
 *
 * @augments JobDefinition
 */
export class ReinventJob extends JobDefinition {
    static jobTypeId = 'reinvent';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'REINVENT 4';
    static jobName = 'Reinvent Job';
    static jobDescription = 'generates new molecules related to the compounds you supply.\n'
        + "It optimizes toward favourable drug-like properties. Results don't contain 3D pose information and aren't scored for binding.";

    static jobCitation = 'https://doi.org/10.1186/s13321-024-00812-5';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /** Results rows are keyed by SMILES rather than the default `id`. */
    static idField = 'SMILES';

    /**
     * Declarative description of this job's form — data only, no
     * rendering. `body` is one of the named content regions `JobForm`
     * renders (see `formHelpers.js` for why they're named rather than an
     * ordered list). This job declares no `header`, and no submit label/
     * tooltip/title — the embedding tab supplies all of that (see
     * `JobForm.jsx`'s `mergeRegion` and its `footer` prop), since this job
     * has no business knowing what a program picker or job-naming field
     * even are, or what a given tab wants its submit button to say.
     * `molDataText` uses the `entryList` widget — `formState.molDataText`
     * is an array of `{ type, data, count }` entries, not a joined blob —
     * the generalized shape shared with (eventually) protein folding's own
     * sequence input, narrowed here to a single supported type (SMILES),
     * which is why no `label` is given for it: with only one type, there's
     * nothing to disambiguate. `withCount` (the entry list's Count column)
     * is deliberately not set here — a repeated ligand doesn't change
     * REINVENT's request — though the widget still supports it for other
     * programs that do care how many copies of an entry they get.
     * `molDataText.initialValueFrom` lets `initData` (compounds
     * pre-selected before this tab was opened) seed the field without a
     * job-specific `getInitialFormState` override.
     * @type {object}
     */
    static jobForm = {
        body: {
            title: 'Specify Compound Input',
            fields: {
                molDataText: {
                    widget: 'entryList',
                    default: [],
                    required: true,
                    requiredMessage: 'Add some input compounds for AI compound generation before submission',
                    addLabel: 'Add Ligand SMILES',
                    types: [
                        {
                            id: 'ligand_smiles',
                            unit: 'compound',
                            unitPlural: 'compounds',
                            placeholder: 'ligand smiles',
                            filetypes: ['smi'],
                        },
                    ],
                    initialValueFrom: (initData) => {
                        if (initData?.selectedCompounds?.length) {
                            return initData.selectedCompounds.map((x) => (
                                { type: 'ligand_smiles', data: x.getUnnamedSmiles() }
                            ));
                        }
                        if (initData?.molDataText) {
                            return initData.molDataText.split('\n').map((s) => s.trim()).filter(Boolean)
                                .map((data) => ({ type: 'ligand_smiles', data }));
                        }
                        return undefined;
                    },
                },
            },
        },
    };

    /**
     * @param {import('../JobDefinition').JobConstructorData} [data]
     */
    constructor(data = {}) {
        super(data);

        // Only keep this if other code expects `job.idField`.
        // Otherwise prefer `this.constructor.idField`.
        this.idField = this.constructor.idField;
    }

    /**
     * Turns the flat payload into the REINVENT job-request payload.
     * @param {object} payload
     * @returns {Promise<{ requestData?: object, error?: string[] }>}
     */
    static async buildRequestPayload(payload) {
        // Lines may carry a trailing name after the SMILES (the common .smi
        // convention, and what pasted/uploaded files often contain) — REINVENT
        // wants bare SMILES only, so take just the first whitespace token.
        const smiles = (payload.molDataText || [])
            .map((entry) => entry.data.trim().split(/\s/)[0])
            .filter((s) => s !== '');
        const errors = this.validateSmiles(smiles);
        if (errors.length > 0) return { error: errors };
        return { requestData: { smiles } };
    }

    /**
     * Flags SMILES using bracketed tokens outside what REINVENT's
     * mol2mol_scaffold_generic model supports.
     * @param {string[]} smilesList
     * @returns {string[]} user-facing error strings, one per offending SMILES
     */
    static validateSmiles(smilesList) {
        const unsupported = smilesList.flatMap((s) => {
            // Extract SMILES part (before any whitespace/name), find
            // bracketed tokens, and flag any not in the allowed list.
            const smilesOnly = s.split(/\s/)[0];
            const bracketedTokens = smilesOnly.match(/\[[^\]]+\]/g) || [];
            const badTokens = bracketedTokens.filter((token) => !ALLOWED_BRACKET_TOKENS.has(token));
            return badTokens.length > 0 ? [{ smiles: s, groups: badTokens }] : [];
        });
        return unsupported.map(
            ({ smiles, groups }) => `Unsupported tokens (${groups.join(', ')}) in SMILES: ${smiles}`
        );
    }

    /**
     * Parses the REINVENT results CSV into structure rows and stores them on
     * local job state.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results) {
        let structures = null;
        if (results.jobResults?.results) {
            structures = Papa.parse(
                results.jobResults.results, { header: true, dynamicTyping: true }
            ).data.map((row) => ({ ...row, smilesText: row.SMILES }));
        }
        updateLocalJobState(job.jobid, { structures });
        return { results };
    }
}
